import type { Activity, ItineraryDay } from '../types';

/**
 * Apply an explicit activity order to a day.
 *
 * The "continues from previous day" origin card is never part of the order the
 * caller supplies — the AI's reorder_day tool has no concept of it and the drag
 * handler leaves it out too. A plain indexOf sort pushes anything it can't find
 * to the end, which silently dropped the origin card into the middle of the day.
 * The backend already re-pins it at sortOrder 0 (see itinerary.controller), so
 * the optimistic update has to agree or the list looks wrong until a reload.
 */
export function applyActivityOrder(activities: Activity[], orderedIds: string[]): Activity[] {
  const sorted = [...activities].sort((a, b) => {
    if (a.isOrigin) return -1;
    if (b.isOrigin) return 1;
    const ai = orderedIds.indexOf(a.id);
    const bi = orderedIds.indexOf(b.id);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  return sorted.map((a, i) => ({ ...a, sortOrder: i }));
}

/**
 * The day's schedule — the optimistic twin of
 * `server/src/services/daySchedule.service.ts`. ADR-0010.
 *
 * It replaced a function that re-dealt the hours a day already had to whatever
 * card landed in each position: drag the 15:00 stop to the front and lunch
 * became three o'clock. This one computes instead — day start, then each stop's
 * duration and the travel to the next.
 *
 * It exists ONLY to paint the change before the round trip lands. The server
 * runs the same cascade and hands back the day it stored, which callers apply
 * on the response, so the two drifting shows up as a corrected render rather
 * than a day that stays wrong until a reload. **Port changes here and there in
 * the same commit** — a second rule is exactly what this ADR forbids.
 */
export const DEFAULT_DURATION_MINS: Record<Activity['type'], number> = {
  RESTAURANT:   75,
  CAFE:         45,
  MONUMENT:     60,
  VIEWPOINT:    30,
  NEIGHBORHOOD: 90,
  PARK:         60,
  SHOP:         45,
  EXPERIENCE:  120,
  TRANSPORT:    30,
  PARKING:      15,
  BEACH:       120,
  NATURE:      120,
  ACCOMMODATION: 30,
  OTHER:        60,
};

const MINS_PER_DAY = 24 * 60;

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export function toHHMM(mins: number): string {
  const capped = Math.min(mins, MINS_PER_DAY - 1);
  return `${String(Math.floor(capped / 60)).padStart(2, '0')}:${String(capped % 60).padStart(2, '0')}`;
}

export function durationOf(a: Pick<Activity, 'durationMins' | 'type'>): number {
  return a.durationMins ?? DEFAULT_DURATION_MINS[a.type] ?? DEFAULT_DURATION_MINS.OTHER;
}

/**
 * The hour a stop ends, for the `08:00–09:30` range on its card.
 *
 * **Only when the duration is real.** An estimate is the least-bad way to place
 * the NEXT stop on a day the user can see is computed; printing it as this
 * stop's end time is a different claim — a number nobody gave, wearing the
 * clothes of one they did. That is ADR-0006's mistake in other clothes, and
 * ADR-0010 names it in its own Consequences.
 */
export function endTimeOf(a: Activity): string | null {
  if (!a.startTime || a.durationMins == null) return null;
  return toHHMM(toMinutes(a.startTime) + a.durationMins);
}

/** A time the app worked out for the user, and the honesty of the number behind it. */
export interface TimeSuggestion {
  time: string;
  /** The previous stop's length was a category guess, not something anyone typed. */
  estimated: boolean;
  /** No cached leg for the hop, so the travel is not in this number. */
  travelUnknown: boolean;
}

/**
 * What time the next stop would start: when the previous one ends, plus the
 * travel between them.
 *
 * ADR-0010 draws the line this sits on. On a day with a computed schedule the
 * hour is already there and this returns null; on a manual day it is arithmetic
 * offered to the user, and **it never writes** — accepting it is a click the
 * person makes. A suggestion that set itself would be a second writer of
 * `startTime`, which is exactly what that ADR forbids.
 *
 * It may rest on an estimated duration, and says so. A suggestion is not an
 * assertion: that is the same reason `endTimeOf` refuses to print an end time
 * from a guess while this one will happily propose from it.
 */
export function suggestedStartTime(
  previous: Activity | undefined,
  travelMins: number | undefined,
  dayStartTime: string | null,
): TimeSuggestion | null {
  if (dayStartTime) return null;
  if (!previous || previous.isOrigin || !previous.startTime) return null;

  const end = toMinutes(previous.startTime) + durationOf(previous);
  return {
    time: toHHMM(end + (travelMins ?? 0)),
    estimated: previous.durationMins == null,
    travelUnknown: travelMins === undefined,
  };
}

/**
 * Recompute a day's hours. `activities` must already be in display order;
 * `travelMinsAfter` is keyed by the stop a leg LEAVES, the same contract as
 * `useDayLegs.secondsAfter`.
 *
 * A day with no start hour comes back untouched — that is what makes this safe
 * to call everywhere, and what keeps every pre-ADR trip exactly as it was.
 */
export function applyDaySchedule(
  dayStartTime: string | null,
  activities: Activity[],
  travelMinsAfter: (activityId: string) => number = () => 0,
): Activity[] {
  if (!dayStartTime) return activities;

  let cursor = toMinutes(dayStartTime);
  return activities.map(a => {
    if (a.isOrigin) {
      // No time of its own, but the travel leaving it is how today reaches its
      // first stop. See the server twin.
      cursor += travelMinsAfter(a.id);
      return a;
    }
    const start = a.startTimeLocked && a.startTime ? toMinutes(a.startTime) : cursor;
    cursor = start + durationOf(a) + travelMinsAfter(a.id);
    return { ...a, startTime: toHHMM(start) };
  });
}

/**
 * Apply the server's rebuilt "continues from previous day" card.
 *
 * Reordering a day changes which stop is its last one, so the next day's origin
 * mirror has to be redrawn. The server does that and hands back the result;
 * applying it here keeps the next day from showing a stale copy until something
 * else happens to refetch. Lives in this module because three callers need it —
 * the itinerary's two views, the map's routes panel and the AI optimiser.
 */
export function applyNextDayUpdate(
  dayList: ItineraryDay[],
  ndu: { dayId: string; originActivity: Activity },
): ItineraryDay[] {
  return dayList.map(d =>
    d.id === ndu.dayId
      ? { ...d, startFromPrevStop: true, activities: [ndu.originActivity, ...d.activities.filter(a => !a.isOrigin)] }
      : d,
  );
}
